import React, { useState } from "react";
import { View, StyleSheet, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { RadioButtonGroup } from "@/components/ui/RadioButtonGroup";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";

export interface ClockOutSurveyData {
  confirmedTimes: boolean;
  receivedMealBreaks: boolean | null;
  receivedRestBreaks: boolean | null;
  overtimeWorked: boolean | null;
  overtimeHours?: string;
  noDutiesOutsideHours: boolean | null;
}

export interface ClockOutSurveyProps {
  startTime?: Date;
  endTime?: Date;
  onComplete?: (data: ClockOutSurveyData) => void;
  onCancel?: () => void;
}

export function ClockOutSurvey({ startTime, endTime, onComplete, onCancel }: ClockOutSurveyProps) {
  const { colors } = useTheme();

  const [data, setData] = useState<ClockOutSurveyData>({
    confirmedTimes: false,
    receivedMealBreaks: null,
    receivedRestBreaks: null,
    overtimeWorked: null,
    overtimeHours: "",
    noDutiesOutsideHours: null,
  });

  const formatTime = (date?: Date) => {
    if (!date) return "--:--";
    return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const updateData = (updates: Partial<ClockOutSurveyData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  };

  const isComplete =
    data.confirmedTimes &&
    data.receivedMealBreaks !== null &&
    data.receivedRestBreaks !== null &&
    data.overtimeWorked !== null &&
    (data.overtimeWorked === false || (data.overtimeWorked === true && data.overtimeHours?.trim() !== "")) &&
    data.noDutiesOutsideHours !== null;

  const handleSubmit = () => {
    if (isComplete && onComplete) {
      onComplete(data);
    }
  };

  const QuestionRow = ({
    question,
    value,
    onChange,
  }: {
    question: string;
    value: boolean | null;
    onChange: (val: boolean) => void;
  }) => (
    <View style={styles.questionRow}>
      <ThemedText style={[styles.questionText, { color: colors.text }]}>{question}</ThemedText>
      <RadioButtonGroup
        options={["Yes", "No"]}
        value={value === null ? undefined : value ? "Yes" : "No"}
        onChange={(item) => onChange(item === "Yes")}
        orientation="horizontal"
        style={styles.toggle}
      />
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <MaterialCommunityIcons name="clipboard-check-outline" size={32} color={colors.primary} />
        <ThemedText style={[styles.title, { color: colors.text }]}>Clock Out Survey</ThemedText>
        <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
          Please confirm the following before completing your shift
        </ThemedText>
      </View>

      {/* Time Confirmation Card */}
      <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="clock-outline" size={24} color={colors.primary} />
          <ThemedText style={[styles.cardTitle, { color: colors.text }]}>Shift Times</ThemedText>
        </View>

        <View style={styles.timeRow}>
          <View style={styles.timeBlock}>
            <ThemedText style={[styles.timeLabel, { color: colors.textSecondary }]}>Start Time</ThemedText>
            <ThemedText style={[styles.timeValue, { color: colors.text }]}>{formatTime(startTime)}</ThemedText>
          </View>
          <View style={[styles.timeDivider, { backgroundColor: colors.border }]} />
          <View style={styles.timeBlock}>
            <ThemedText style={[styles.timeLabel, { color: colors.textSecondary }]}>End Time</ThemedText>
            <ThemedText style={[styles.timeValue, { color: colors.text }]}>{formatTime(endTime)}</ThemedText>
          </View>
        </View>

        <View style={styles.confirmRow}>
          <Button
            text={data.confirmedTimes ? "Times Confirmed" : "Confirm Times"}
            icon={data.confirmedTimes ? "check" : undefined}
            onPress={() => updateData({ confirmedTimes: !data.confirmedTimes })}
            style={[
              styles.confirmButton,
              {
                backgroundColor: data.confirmedTimes ? colors.emerald?.background : "transparent",
                borderColor: data.confirmedTimes ? colors.emerald?.border : colors.border,
              },
            ]}
            textStyle={{ color: data.confirmedTimes ? colors.emerald?.text : colors.text }}
          />
        </View>
      </View>

      {/* Questions Card */}
      <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="help-circle-outline" size={24} color={colors.primary} />
          <ThemedText style={[styles.cardTitle, { color: colors.text }]}>Survey Questions</ThemedText>
        </View>

        <View style={styles.questionsList}>
          <QuestionRow
            question="Did you receive proper meal breaks according to guidelines?"
            value={data.receivedMealBreaks}
            onChange={(val) => updateData({ receivedMealBreaks: val })}
          />

          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          <QuestionRow
            question="Did you receive proper rest breaks according to guidelines?"
            value={data.receivedRestBreaks}
            onChange={(val) => updateData({ receivedRestBreaks: val })}
          />

          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          <QuestionRow
            question="Was overtime worked?"
            value={data.overtimeWorked}
            onChange={(val) => updateData({ overtimeWorked: val, overtimeHours: "" })}
          />

          {data.overtimeWorked === true && (
            <View style={styles.overtimeInput}>
              <ThemedText style={[styles.overtimeLabel, { color: colors.textSecondary }]}>
                How much overtime was worked?
              </ThemedText>
              <TextInput
                style={[
                  styles.textInput,
                  {
                    backgroundColor: colors.backgroundSecondary,
                    borderColor: colors.border,
                    color: colors.text,
                  },
                ]}
                placeholder="e.g., 2 hours"
                placeholderTextColor={colors.placeholder}
                value={data.overtimeHours}
                onChangeText={(text) => updateData({ overtimeHours: text })}
              />
            </View>
          )}

          <View style={[styles.divider, { backgroundColor: colors.border }]} />

          <QuestionRow
            question="Confirm no duties were performed outside of working hours"
            value={data.noDutiesOutsideHours}
            onChange={(val) => updateData({ noDutiesOutsideHours: val })}
          />
        </View>
      </View>

      {/* Action Buttons */}
      <View style={styles.buttonRow}>
        {onCancel && (
          <Button
            text="Cancel"
            onPress={onCancel}
            style={[styles.cancelButton, { borderColor: colors.border }]}
            textStyle={{ color: colors.text }}
          />
        )}
        <Button
          text="Submit"
          icon="check"
          iconColor="white"
          onPress={handleSubmit}
          enabled={isComplete}
          style={[
            styles.submitButton,
            {
              backgroundColor: isComplete ? colors.primary : colors.border,
              opacity: isComplete ? 1 : 0.5,
              flex: onCancel ? 2 : 1,
            },
          ]}
          textStyle={{ color: "white", fontWeight: "600" }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 20,
  },
  header: {
    alignItems: "center",
    gap: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 14,
    textAlign: "center",
    lineHeight: 20,
  },
  card: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    gap: 16,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  timeRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  timeBlock: {
    flex: 1,
    alignItems: "center",
    gap: 4,
  },
  timeLabel: {
    fontSize: 12,
    textTransform: "uppercase",
  },
  timeValue: {
    fontSize: 24,
    fontWeight: "600",
  },
  timeDivider: {
    width: 1,
    height: 40,
    marginHorizontal: 16,
  },
  confirmRow: {
    alignItems: "center",
  },
  confirmButton: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 24,
    minHeight: 44,
  },
  questionsList: {
    gap: 4,
  },
  questionRow: {
    gap: 10,
    paddingVertical: 8,
  },
  questionText: {
    fontSize: 14,
    lineHeight: 20,
  },
  toggle: {
    alignSelf: "flex-start",
  },
  divider: {
    height: 1,
    marginVertical: 8,
  },
  overtimeInput: {
    gap: 8,
    paddingLeft: 16,
  },
  overtimeLabel: {
    fontSize: 13,
  },
  textInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    borderRadius: 8,
    minHeight: 48,
    borderWidth: 1,
    backgroundColor: "transparent",
  },
  submitButton: {
    borderRadius: 8,
    minHeight: 48,
  },
});
